import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTags } from '../../hooks/useTags';
import Fuse from 'fuse.js';
import { cn } from '../../helpers/classnames';
import Chip from '../core/Chip';
import Input from '../core/Input';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl';

interface TagSelectorProps {
  selectedTagIds: string[];
  onChange?: (selectedTagIds: string[]) => void;
  onTagClick?: (tagId: string, selected: boolean) => void;
  excludeUneditable?: boolean;
  className?: string;
}

const messages = defineMessages({
  searchTags: { id: 'search', defaultMessage: 'Search' },
});

const TagSelector: FC<TagSelectorProps> = ({
  selectedTagIds,
  onChange,
  onTagClick,
  className,
  excludeUneditable,
}) => {
  const intl = useIntl();
  const { list: allTags } = useTags();
  const [inputText, setInputText] = useState<string>('');

  const availableTags = useMemo(() => {
    if (excludeUneditable) {
      return allTags.filter((t) => t.editable);
    }
    return allTags;
  }, [allTags, excludeUneditable]);

  const tagFuse = useMemo(
    () =>
      new Fuse(availableTags, {
        keys: ['name'],
      }),
    [availableTags],
  );

  const options = useMemo(() => {
    return availableTags.map((t) => ({
      id: t.id,
      name: t.name,
      description: t.description,
    }));
  }, [availableTags]);

  const filteredOptions = useMemo(() => {
    if (!inputText) {
      return options;
    }
    return tagFuse.search(inputText).map((result) => result.item);
  }, [options, inputText, tagFuse]);

  const onTagSelect = useCallback(
    (tagId: string) => {
      if (onChange) {
        const ids = [...selectedTagIds];
        if (!ids.includes(tagId)) {
          ids.push(tagId);
        }
        onChange(ids);
      }
      if (onTagClick) {
        onTagClick(tagId, false);
      }
    },
    [onChange, onTagClick, selectedTagIds],
  );
  const onTagDeselect = useCallback(
    (tagId: string) => {
      if (onChange) {
        const ids = [...selectedTagIds];
        const index = ids.findIndex((t) => t === tagId);
        if (index > -1) {
          ids.splice(index, 1);
        }
        onChange(ids);
      }
      if (onTagClick) {
        onTagClick(tagId, true);
      }
    },
    [onChange, onTagClick, selectedTagIds],
  );

  return (
    <div className={cn('flex flex-col', className)}>
      <Input
        id="tag-selector-input"
        type="text"
        placeholder={intl.formatMessage(messages.searchTags)}
        onChange={(ev) => setInputText(ev.target.value)}
        className="mb-1"
      />
      <div className="flex flex-wrap gap-1">
        {filteredOptions.map((t) => {
          const selected = selectedTagIds.includes(t.id);
          return (
            <Chip
              key={t.id}
              name={t.name}
              description={t.description}
              filled={selected}
              onClick={() =>
                selected ? onTagDeselect(t.id) : onTagSelect(t.id)
              }
            />
          );
        })}
      </div>
    </div>
  );
};

export default TagSelector;
